<script setup lang="ts">
import { OrderDetails } from '@/Pejer/types/checkout'
import DataStateSkeleton from 'pejer/src/components/DataState/DataStateSkeleton.vue'
import Separator from 'pejer/src/components/ui/separator/Separator.vue'
import Skeleton from 'pejer/src/components/ui/skeleton/Skeleton.vue'
import { MINOR_UNIT_FACTOR } from 'pejer/src/Consts'
import { DataState } from 'pejer/src/types/types'
import { computed, ref, unref, watch } from 'vue'

const props = defineProps<{
  orderDetails?: OrderDetails
  description?: string
  currency?: string
  orderTotal?: number
  dataState: DataState
}>()

const orderTax = computed(() => {
  return props.orderDetails?.productLines.reduce(
    (acc, item) => acc + item.grossCost * (item.taxPercentage / 100),
    0
  )
})

const brokenImages = ref<Set<number>>(new Set())
function onImgError(id: number) {
  brokenImages.value.add(id)
}

const loadingImages = ref<Set<number>>(new Set())

watch(
  () => props.orderDetails?.productLines,
  (newLines) => {
    if (!newLines) return
    newLines.forEach((line, index) => {
      if (line.imageUrl && !loadingImages.value.has(index)) {
        loadingImages.value.add(index)
      }
    })
  },
  { immediate: true }
)

function onImgLoad(id: number) {
  loadingImages.value.delete(id)
}

const showImageColumn = computed(() => {
  return (
    props.orderDetails?.productLines.some((item) => item.imageUrl) &&
    brokenImages.value.size !== props.orderDetails?.productLines.length
  )
})

const isLoading = computed(() => {
  return unref(props.dataState.isLoading)
})
const isError = computed(() => {
  return unref(props.dataState.isError)
})

function getAmountString(amount: number | null) {
  if (!amount) return ''
  return new Intl.NumberFormat('no-NO', {
    style: 'currency',
    currency: props.currency,
    minimumFractionDigits: 2,
  }).format(amount / MINOR_UNIT_FACTOR)
}
</script>
<template>
  <div class="w-full max-h-full min-h-full text-merino-950 gap-4 flex flex-col justify-start">
    <!-- rounded-xl border-slate-200 border p-8 bg-white -->
    <span class="w-full s pb-2 text-merino-950/60">
      <h1 class="text-xs font-medium">Ordredetaljer</h1>
      <DataStateSkeleton
        :data-state="dataState"
        sizing-class="w-1/2 h-6 mt-1"
        class="bg-merino-200"
        error-class="bg-merino-200"
      >
        <p class="text-xl font-medium text-merino-950">{{ description }}</p>
      </DataStateSkeleton>
    </span>
    <div class="flex flex-col gap-3 w-full min-h-0" v-if="!isLoading && !isError">
      <div
        v-for="(item, index) in orderDetails.productLines"
        :key="index"
        class="flex gap-2 items-center justify-between w-full bg-white border-slate-300 border rounded-sm p-3 shadow-md shadow-merino-200"
      >
        <!-- :class="{
          'border-b border-slate-200/60': index < orderDetails.productLines.length - 1,
        }" -->
        <div class="flex flex-col sm:flex-row gap-x-4 gap-y-2 w-full">
          <div
            class="rounded-sm overflow-hidden shrink-0 w-[90px] aspect-square"
            v-if="showImageColumn"
          >
            <div
              v-if="loadingImages.has(index)"
              class="w-full h-full flex flex-col items-center justify-center rounded-sm"
            >
              <Skeleton class="w-full h-full bg-slate-200" />
            </div>
            <img
              v-if="!brokenImages.has(index)"
              :src="item.imageUrl"
              @error="onImgError(index)"
              @load="onImgLoad(index)"
              alt="Product Image"
              class="w-full object-cover rounded-sm aspect-square"
              :class="{
                hidden: loadingImages.has(index),
              }"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center text-slate-300 rounded-sm"
            >
              <span class="text-xs">No image</span>
            </div>
          </div>
          <div class="flex flex-col text-slate-700 justify-center pt-1 w-fit">
            <span class="flex items-baseline">
              <!-- border-b border-slate-300 w-full pb-0.5 -->
              <h2 class="text-md font-medium">
                {{ item.name }}
              </h2>
              <!-- variant="secondary" -->
              <span v-if="item.quantity !== 1">
                <span class="font-thin ml-1">•</span>
                <span class="text-xs text-slate-500 ml-1 font-semibold">
                  {{ item.quantity }}x
                </span>
              </span>
            </span>
            <!-- <Separator class="mb-2" /> -->
            <span
              v-if="item.description"
              class="text-xs text-slate-500 min-h-[2em] font-normal w-full"
            >
              {{ item.description }}
            </span>
            <!-- TODO: uncomment this when backend is deployed, 02.09.2025 -->
            <!-- <Badge
              v-if="item.productID"
              variant="secondary"
              class="text-xs text-slate-500 font-normal mt-auto"
            >
              {{ item.productID }}
            </Badge> -->
          </div>
        </div>
        <div
          class="text-sm font-semibold items-center rounded-md px-2 py-1 text-slate-700 pr-2 pl-2"
        >
          <!--  border border-slate-200 bg-slate-50 -->
          {{ getAmountString(item.grossCost) }}
        </div>
      </div>
    </div>
    <div v-else class="w-full h-full flex flex-col gap-3">
      <Skeleton class="w-full h-[116px] bg-merino-200" />
      <Skeleton class="w-full h-[116px] bg-merino-200" />
    </div>
    <div class="flex flex-col gap-2 mt-auto min-h-[90px] justify-end">
      <Separator v-if="!isLoading && !isError" class="w-full shrink-0 my-2 bg-merino-200 h-[1px]" />
      <div class="flex flex-col gap-1 w-full px-4">
        <div
          class="flex gap-4 items-center justify-between w-full text-xs text-merino-950/60"
          v-if="!isLoading && !isError && orderDetails.grossShippingCost > 0"
        >
          <span class="flex gap-1 items-center font-medium">
            <!-- <Boxes class="w-4 h-4" /> -->
            <h2 class="">Varer</h2>
          </span>
          <div class="font-semibold">
            {{ getAmountString(orderTotal - orderDetails.grossShippingCost) }}
          </div>
        </div>
        <div
          class="flex gap-4 items-center justify-between w-full text-xs text-merino-950/60"
          v-if="!isLoading && !isError && orderDetails.grossShippingCost > 0"
        >
          <span class="flex gap-1 items-center font-medium">
            <!-- <Truck class="w-4 h-4" /> -->
            <h2 class="">Frakt</h2>
          </span>
          <div class="font-semibold">
            {{ getAmountString(orderDetails.grossShippingCost) }}
          </div>
        </div>
        <div class="flex gap-4 items-center justify-between w-full">
          <h2 class="text-md font-semibold">Total</h2>
          <div class="text-md font-semibold">
            <DataStateSkeleton
              :data-state="dataState"
              sizing-class="w-16 h-6"
              class="bg-merino-200"
              error-class="bg-merino-200"
            >
              <span>{{ getAmountString(orderTotal) }}</span>
            </DataStateSkeleton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
